export enum NotificationPriority {
  CRITICAL = 'critical',
  URGENT = 'urgent',
  LOW = 'low',
  SUCCESS = 'success',
}

/**
 * Backend-supplied category. Mirrors `NotificationCategory` in the data layer enum.
 * Display labels are mapped at the component layer in `useNotifications`.
 */
export enum NotificationCategory {
  APPOINTMENTS = 'appointments',
  NOTES = 'notes',
  FORMS = 'forms',
  CHARTING = 'charting',
  CLAIMS = 'claims',
  CLINICS = 'clinics',
  BILLING = 'billing',
  MESSAGE_CENTER = 'message_center',
  FAX = 'fax',
  EMAIL = 'email',
}

/**
 * Stable trigger keys, mirrors `NotificationTriggerType` on the backend.
 * Use it when narrowing `notification.type` for module-specific behaviour.
 */
export enum NotificationTriggerType {
  // Appointment Scheduling
  APPOINTMENT_DOUBLE_BOOKING = 'APPOINTMENT_DOUBLE_BOOKING',
  APPOINTMENT_PROVIDER_TIME_BLOCK_VIOLATION = 'APPOINTMENT_PROVIDER_TIME_BLOCK_VIOLATION',
  APPOINTMENT_NEW_ONLINE_BOOKED = 'APPOINTMENT_NEW_ONLINE_BOOKED',
  APPOINTMENT_CONFIRMATION_DELIVERY = 'APPOINTMENT_CONFIRMATION_DELIVERY',
  APPOINTMENT_AI_HIGH_RISK_CANCELLATION = 'APPOINTMENT_AI_HIGH_RISK_CANCELLATION',
  APPOINTMENT_WAITLIST_MATCH_FOUND = 'APPOINTMENT_WAITLIST_MATCH_FOUND',
  APPOINTMENT_TIME_MODIFIED = 'APPOINTMENT_TIME_MODIFIED',
  APPOINTMENT_PATIENT_CONFIRMATION_RESPONSE = 'APPOINTMENT_PATIENT_CONFIRMATION_RESPONSE',
  APPOINTMENT_OVERLAP_CROSS_LOCATION = 'APPOINTMENT_OVERLAP_CROSS_LOCATION',
  APPOINTMENT_AI_BOOKED = 'APPOINTMENT_AI_BOOKED',
  APPOINTMENT_AI_RESCHEDULED = 'APPOINTMENT_AI_RESCHEDULED',
  APPOINTMENT_AI_CANCELLED = 'APPOINTMENT_AI_CANCELLED',
  PATIENT_AI_CREATED = 'PATIENT_AI_CREATED',

  // Notes
  NOTE_MEDICAL_ALERT_ADDED = 'NOTE_MEDICAL_ALERT_ADDED',
  NOTE_URGENT_FINANCIAL_ADDED = 'NOTE_URGENT_FINANCIAL_ADDED',
  NOTE_REQUIRES_CO_SIGNATURE = 'NOTE_REQUIRES_CO_SIGNATURE',
  NOTE_PATIENT_PREFERENCE_ADDED = 'NOTE_PATIENT_PREFERENCE_ADDED',
  NOTE_FOLLOWUP_DUE = 'NOTE_FOLLOWUP_DUE',
  NOTE_REFERRAL_SOURCE_CONTACT = 'NOTE_REFERRAL_SOURCE_CONTACT',
  NOTE_EDITED_ARCHIVED = 'NOTE_EDITED_ARCHIVED',

  // Forms
  FORM_CRITICAL_NOT_SIGNED = 'FORM_CRITICAL_NOT_SIGNED',
  FORM_SUBMISSION_ERROR = 'FORM_SUBMISSION_ERROR',
  FORM_PENDING_PRE_APPOINTMENT = 'FORM_PENDING_PRE_APPOINTMENT',
  FORM_NEW_PATIENT_SUBMISSION = 'FORM_NEW_PATIENT_SUBMISSION',
  FORM_LOCKED_ACCESS_EXPIRED = 'FORM_LOCKED_ACCESS_EXPIRED',
  FORM_PATIENT_REQUESTED_CREATION = 'FORM_PATIENT_REQUESTED_CREATION',
  FORM_UPDATED_TEMPLATE_PUBLISHED = 'FORM_UPDATED_TEMPLATE_PUBLISHED',
  FORM_COMPLETED = 'FORM_COMPLETED',

  // Charting / Treatment
  CHART_UNSIGNED_NOTE = 'CHART_UNSIGNED_NOTE',
  CHART_TREATMENT_PLAN_OVER_THRESHOLD = 'CHART_TREATMENT_PLAN_OVER_THRESHOLD',
  CHART_LAB_CASE_INTEGRATION_MISSING = 'CHART_LAB_CASE_INTEGRATION_MISSING',
  CHART_TREATMENT_PLAN_PENDING_ACCEPTANCE = 'CHART_TREATMENT_PLAN_PENDING_ACCEPTANCE',
  CHART_DATA_CONFLICT_DETECTED = 'CHART_DATA_CONFLICT_DETECTED',
  CHART_NEW_CONDITION_ADDED = 'CHART_NEW_CONDITION_ADDED',
  CHART_ENTRY_EDITED = 'CHART_ENTRY_EDITED',

  // Claims
  CLAIM_REJECTED = 'CLAIM_REJECTED',
  CLAIM_DENIED = 'CLAIM_DENIED',
  CLAIM_PENDING_OVER_30_DAYS = 'CLAIM_PENDING_OVER_30_DAYS',
  CLAIM_PAYMENT_DISCREPANCY = 'CLAIM_PAYMENT_DISCREPANCY',
  CLAIM_ATTACHMENTS_REQUIRED = 'CLAIM_ATTACHMENTS_REQUIRED',
  CLAIM_FINALIZED = 'CLAIM_FINALIZED',
  CLAIM_NEW_PAYER_FEE_SCHEDULE = 'CLAIM_NEW_PAYER_FEE_SCHEDULE',
  CLAIM_BATCH_SUBMISSION_FAILED = 'CLAIM_BATCH_SUBMISSION_FAILED',

  // Locations
  LOCATION_FEE_SCHEDULE_SYNC_FAILURE = 'LOCATION_FEE_SCHEDULE_SYNC_FAILURE',
  LOCATION_SYSTEM_OFFLINE = 'LOCATION_SYSTEM_OFFLINE',
  LOCATION_SETTINGS_EDITED = 'LOCATION_SETTINGS_EDITED',
  LOCATION_PENDING_FORMS_UPDATE = 'LOCATION_PENDING_FORMS_UPDATE',
  LOCATION_DAILY_TARGET_MISSED = 'LOCATION_DAILY_TARGET_MISSED',
  LOCATION_SOFTWARE_UPDATE_AVAILABLE = 'LOCATION_SOFTWARE_UPDATE_AVAILABLE',
  LOCATION_NEW_LOCATION_ADDED = 'LOCATION_NEW_LOCATION_ADDED',

  // Port requests
  PORT_REQUEST_SUBMITTED = 'PORT_REQUEST_SUBMITTED',
  PORT_REQUEST_APPROVED = 'PORT_REQUEST_APPROVED',
  PORT_REQUEST_DECLINED = 'PORT_REQUEST_DECLINED',
  PORT_REQUEST_RESUBMITTED = 'PORT_REQUEST_RESUBMITTED',

  // Billing
  BILLING_PAYMENT_PROCESSOR_FAILURE = 'BILLING_PAYMENT_PROCESSOR_FAILURE',
  BILLING_CARD_DECLINED = 'BILLING_CARD_DECLINED',
  BILLING_LARGE_UNAPPLIED_PAYMENT = 'BILLING_LARGE_UNAPPLIED_PAYMENT',
  BILLING_ACCOUNT_PAST_DUE = 'BILLING_ACCOUNT_PAST_DUE',
  BILLING_DAILY_POSTING_INCOMPLETE = 'BILLING_DAILY_POSTING_INCOMPLETE',
  BILLING_PAYMENT_PLAN_DUE_TODAY = 'BILLING_PAYMENT_PLAN_DUE_TODAY',
  BILLING_STATEMENT_RUN_COMPLETE = 'BILLING_STATEMENT_RUN_COMPLETE',

  // Message Center
  MESSAGE_EMERGENCY_RECEIVED = 'MESSAGE_EMERGENCY_RECEIVED',
  MESSAGE_NEW_UNREAD = 'MESSAGE_NEW_UNREAD',
  MESSAGE_MISSED_CALL_NO_MATCH = 'MESSAGE_MISSED_CALL_NO_MATCH',
  MESSAGE_OPT_OUT_RECEIVED = 'MESSAGE_OPT_OUT_RECEIVED',
  MESSAGE_INTERNAL_TEAM = 'MESSAGE_INTERNAL_TEAM',
  MESSAGE_AUTOMATED_FAILED = 'MESSAGE_AUTOMATED_FAILED',

  // Fax
  FAX_NEW_RECEIVED = 'FAX_NEW_RECEIVED',
  FAX_DELIVERY_FAILED = 'FAX_DELIVERY_FAILED',

  // Email
  EMAIL_NEW_RECEIVED = 'EMAIL_NEW_RECEIVED',
  EMAIL_REPLY_RECEIVED = 'EMAIL_REPLY_RECEIVED',
}

/**
 * Display labels for the backend `NotificationCategory` values. Used by the page
 * filters and any UI surface that wants to show a human-friendly category label
 * without calling `getNotificationCategory()` (the legacy heuristic for mocks).
 */
export const NotificationCategoryLabel: Record<NotificationCategory, string> = {
  [NotificationCategory.APPOINTMENTS]: 'Appointments',
  [NotificationCategory.NOTES]: 'Notes',
  [NotificationCategory.FORMS]: 'Forms',
  [NotificationCategory.CHARTING]: 'Charting',
  [NotificationCategory.CLAIMS]: 'Claims',
  [NotificationCategory.CLINICS]: 'Clinics',
  [NotificationCategory.BILLING]: 'Billing',
  [NotificationCategory.MESSAGE_CENTER]: 'Message Center',
  [NotificationCategory.FAX]: 'Fax',
  [NotificationCategory.EMAIL]: 'Email',
}

export interface Notification {
  /** Existing UI shape (kept for backwards compatibility). */
  id: string
  priority: NotificationPriority
  title: string
  date: string
  time: string
  message: string
  actionButtonText: string
  patient_id?: string

  /** Backend-augmented fields (present for items hydrated from the API). */
  notification_id?: string
  category?: NotificationCategory
  type?: NotificationTriggerType | string
  clinic_id?: string
  action_url?: string | null
  action_metadata?: Record<string, unknown> | null
  source_module?: string | null
  source_record_id?: string | null
  is_read?: boolean
  dismissed?: boolean
  created_at?: string
}

/**
 * Raw shape returned by the BL `notification.service.ts`.
 * The frontend api layer maps this to the UI-friendly `Notification` shape above.
 */
export interface BackendNotification {
  notificationId: string
  clinicId: string
  patientId: string | null
  priority: NotificationPriority | string
  category: NotificationCategory | string
  type: string
  title: string
  message: string
  actionButtonText: string | null
  actionUrl: string | null
  actionMetadata: Record<string, unknown> | null
  sourceModule: string | null
  sourceRecordId: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  isRead?: boolean
  dismissed?: boolean
  readAt?: string | null
  dismissedAt?: string | null
}

export interface NotificationCounts {
  critical: number
  urgent: number
  low: number
  total: number
  unread: number
}
